import { uniq } from 'lodash';
import { constructArtifactPath, getNumCores } from 'utils/conchUtils';
import { v4 as uuidv4 } from 'uuid';

import { MARKET_SURVEY_CONSTANTS } from './constants';
import { CONSTRAINT_LEVELS } from '../model/constants';
import {
  ParentReport,
  ReportRequestState,
  UpdateSource,
} from '../model/types';

export interface BuildContext {
  requestedBy: string;
  jobDefinition: conch.JobDefinition;
  /** Injectable for deterministic tests. */
  generateId?: () => string;
}

export type ReportParamsV2 =
  | conch.MarketSurveyReportParams
  | conch.OptimizationReportParams
  | conch.UpdateReportParams;

const OPTIMIZATION_CORES = 15;

const productNames = (state: ReportRequestState) =>
  uniq(state.products.map((p) => p.name));

const nullIfBlank = (value: string): string | null =>
  value.trim().length > 0 ? value.trim() : null;

/** Matches the target group naming conch has always received, e.g. "Usa Sans Rejectors". */
export const marketSurveyTargetGroupName = (
  country: string,
  sansRejector: boolean,
): string =>
  (' ' + country + (sansRejector ? ' sans rejectors' : ''))
    .replace(/\s\w/g, (c) => c.toUpperCase())
    .trim();

export const parentArtifactPath = (
  parent: ParentReport,
  efsProjectName?: string | null,
): string =>
  constructArtifactPath(
    parent.startedAt,
    parent.clientName,
    efsProjectName ?? parent.projectName,
    parent.targetGroupName,
  );

export const updateArtifactPath = (
  source: UpdateSource,
  clientName: string,
  projectName: string,
): string =>
  constructArtifactPath(
    source.startedAt,
    clientName,
    projectName,
    source.targetGroupName,
  );

/** Ordered high -> low as conch expects, regardless of the order the user clicked. */
const orderedConstraintLevels = (state: ReportRequestState): string[] =>
  CONSTRAINT_LEVELS.map((c) => c.level).filter((level) =>
    state.optimization.constraintLevels.includes(level),
  );

/** One pixel per line; pixel names themselves contain commas, e.g. `Pixel: 4.49 PQ, (r89,g12,b4)`. */
const pixelList = (raw: string): string[] =>
  raw
    .split('\n')
    .map((pixel) => pixel.trim())
    .filter(Boolean);

export const buildMarketSurveyParams = (
  state: ReportRequestState,
  ctx: BuildContext,
): conch.MarketSurveyReportParams => {
  const generateId = ctx.generateId ?? uuidv4;
  const names = productNames(state);
  const country = state.country ?? '';
  const advanced = state.advanced;
  return {
    ...MARKET_SURVEY_CONSTANTS,
    report_type: 'market_survey',
    reportUID: generateId(),
    job_definition: ctx.jobDefinition,
    requestedBy: ctx.requestedBy,
    client_name: state.clientName.trim(),
    project_name: state.projectName.trim(),
    workspace_id: state.workspace?.id,
    target_group_name: marketSurveyTargetGroupName(country, state.sansRejector),
    client_allowed_workspaces: [...state.allowedWorkspaces],

    countries: [country],
    usa_region: null,
    clts: [...state.clts],
    smoking_habits: state.smokingHabits,
    age_ranges: state.ageRanges.map(({ min, max, fraction }) => ({
      min,
      max,
      fraction,
    })),
    include_female: state.includeFemale,
    include_male: Number((1 - state.includeFemale).toFixed(4)),
    SansRejector: state.sansRejector,
    ...state.ethnicities,

    competitive_set_products: names,
    competitive_set_folders: uniq(state.categories),
    custom_class_db_name: state.customClassDbName.trim(),
    custom_class_db_products: uniq(state.customClassDbProducts),

    profilingEnabled: advanced.profilingEnabled,
    generate_ppt: advanced.generate_ppt,
    include_texture: advanced.include_texture,
    texture_only: advanced.texture_only,
    texture_data_only: advanced.texture_data_only,
    showConfidence: advanced.showConfidence,
    show_TC_se: advanced.show_TC_se,
    include_archetypes: advanced.include_archetypes,
    plotNorm: advanced.plotNorm,
    combine_artificial_sweeteners: advanced.combine_artificial_sweeteners,
    wipe_out_imputed_ggvars: advanced.wipe_out_imputed_ggvars,
    bootstrap_pq: advanced.bootstrap_pq,
    includeNoveltyScores: advanced.includeNoveltyScores,
    only_include_parents: advanced.only_include_parents,
    experimental_features: advanced.experimental_features,
    use_hyperlocal_glp: advanced.use_hyperlocal_glp,

    numCores: getNumCores(state.categories, names, advanced.bootstrap_pq),
    report_id_to_inherit_parent_data_sources: nullIfBlank(
      state.reportIdToInherit,
    ),
  };
};

/** The parent report's identity, which an optimization reuses. */
const parentFields = (state: ReportRequestState) => {
  const parent = state.parent;
  return {
    parent_job_id: parent?.jobId,
    artifact_path: parent
      ? parentArtifactPath(parent, state.inherited?.projectName)
      : undefined,
    workspace_id: parent?.workspaceId,
    project_name: parent?.projectName,
    client_name: parent?.clientName,
    target_group_name: parent?.targetGroupName,
  };
};

/** Demographics and competitive set, copied verbatim from the parent. */
const inheritedFields = (state: ReportRequestState) => {
  const inherited = state.inherited;
  return {
    countries: inherited?.countries ?? [],
    age_ranges: inherited?.ageRanges ?? [],
    include_male: inherited?.includeMale ?? 0,
    include_female: inherited?.includeFemale ?? 0,
    SansRejector: inherited?.sansRejector ?? false,
    custom_class_db_name: inherited?.customClassDbName ?? null,
    competitive_set_folders: inherited?.competitiveSetFolders ?? [],
    competitive_set_products: uniq(inherited?.competitiveSetProducts ?? []),
  };
};

/** Fields shared by optimization and mix & match, inherited from the parent. */
const optimizationBase = (state: ReportRequestState, ctx: BuildContext) => {
  const generateId = ctx.generateId ?? uuidv4;
  return {
    report_type: 'optimization' as const,
    reportUID: generateId(),
    numCores: OPTIMIZATION_CORES,
    job_definition: ctx.jobDefinition,
    requestedBy: ctx.requestedBy,
    ...parentFields(state),
    products_to_opt: state.optimization.targetProduct
      ? [state.optimization.targetProduct]
      : [],
    ...inheritedFields(state),
    add_or_subtract: 'lock' as const,
    include_gradients: true as const,
    include_archetypes: false as const,
    profilingEnabled: false as const,
    slack_me: true,
    opt_file_to_load: null,
    report_id_to_inherit_parent_data_sources: nullIfBlank(
      state.reportIdToInherit,
    ),
  };
};

export const buildOptimizationParams = (
  state: ReportRequestState,
  ctx: BuildContext,
): conch.OptimizationReportParams => {
  const opt = state.optimization;
  return {
    ...optimizationBase(state, ctx),
    ggvar_to_lock: opt.ggvarConstraints.map((c) => c.key),
    ggvar_min_list: opt.ggvarConstraints.map((c) => Math.min(c.min, c.max)),
    ggvar_max_list: opt.ggvarConstraints.map((c) => Math.max(c.min, c.max)),
    constraint_levels: orderedConstraintLevels(state),
    new_reference_flavors_allowed: (['TRUE', 'FALSE'] as const).filter((v) =>
      opt.newReferenceFlavors.includes(v),
    ),
    rangeReff: opt.refFlavorsToTune.map((t) => t.flavor),
    rangeReffRange: {
      min: opt.refFlavorsToTune.map((t) => Math.min(t.min, t.max) / 100),
      max: opt.refFlavorsToTune.map((t) => Math.max(t.min, t.max) / 100),
    },
    ref_flavor_to_lock: [...opt.refFlavorsToLock],
    flavors_to_remove: [...opt.refFlavorsToRemove],
    show_pg_gNorm: opt.showNorms,
    plotNorm: opt.plotNorm,
    show_tuning_plots: opt.tuningPlots,
    pixel_list: pixelList(opt.pixelList),
    run_flavor_opt: opt.optimizeFlavor,
    run_texture_opt: opt.optimizeTexture,
  };
};

export const buildMixAndMatchParams = (
  state: ReportRequestState,
  ctx: BuildContext,
): conch.OptimizationReportParams => ({
  ...optimizationBase(state, ctx),
  ggvar_to_lock: [],
  ggvar_min_list: [],
  ggvar_max_list: [],
  constraint_levels: ['HC', 'MC', 'LC'],
  new_reference_flavors_allowed: ['FALSE'],
  rangeReff: [],
  rangeReffRange: { min: [], max: [] },
  ref_flavor_to_lock: [],
  flavors_to_remove: [],
  show_pg_gNorm: false,
  plotNorm: false,
  show_tuning_plots: true,
  pixel_list: [],
  run_flavor_opt: false,
  run_texture_opt: false,
  mix_and_match: state.mixAndMatch.map((m) => ({
    report_id: m.reportId,
    artifact_path: m.artifactPath,
    versions: [...m.versions],
  })),
});

export const buildUpdateProductsParams = (
  state: ReportRequestState,
  ctx: BuildContext,
): conch.UpdateReportParams => {
  const generateId = ctx.generateId ?? uuidv4;
  const source = state.updateSource;
  const names = productNames(state);
  const original = source?.originalProducts ?? [];
  const originalWorkspaces = source?.originalWorkspaces ?? [];
  const clientName = state.clientName.trim();
  const projectName = state.projectName.trim();
  return {
    report_type: 'update_products',
    reportUID: generateId(),
    job_definition: ctx.jobDefinition,
    requestedBy: ctx.requestedBy,
    client_name: clientName,
    project_name: projectName,
    target_group_name: source?.targetGroupName,
    workspace_id: state.workspace?.id,
    parent_job_id: source?.jobId,
    artifact_path: source
      ? updateArtifactPath(source, clientName, projectName)
      : undefined,
    original_products: [...original],
    incoming_products: names.filter((n) => !original.includes(n)),
    competitive_set_products: uniq([...original, ...names]),
    client_allowed_workspaces: [...state.allowedWorkspaces],
    incoming_workspaces: state.allowedWorkspaces.filter(
      (w) => !originalWorkspaces.includes(w),
    ),
    only_new_products_on_report: false,
    ensure_exact_pq: state.updateStrategy === 'ensure_exact_pq',
    version_controled_translation:
      state.updateStrategy === 'version_controled_translation',
    plotNorm: state.advanced.plotNorm,
    wipe_out_imputed_ggvars: state.advanced.wipe_out_imputed_ggvars,
    profilingEnabled: state.advanced.profilingEnabled,
    numCores: getNumCores(state.categories, names, state.advanced.bootstrap_pq),
    report_id_to_inherit_parent_data_sources: nullIfBlank(
      state.reportIdToInherit,
    ),
    slack_me: true,
    countries: source?.countries ?? [],
    age_ranges: source?.ageRanges ?? [],
    include_male: source?.includeMale ?? 0,
    include_female: source?.includeFemale ?? 0,
    SansRejector: source?.sansRejector ?? false,
    custom_class_db_name: nullIfBlank(state.customClassDbName),
    competitive_set_folders: uniq([...(source?.originalCategories ?? []), ...state.categories]),
    show_pg_gNorm: false,
  };
};

export const buildReportParams = (
  state: ReportRequestState,
  ctx: BuildContext,
): ReportParamsV2 | null => {
  switch (state.reportKind) {
    case 'market_survey':
      return buildMarketSurveyParams(state, ctx);
    case 'optimization':
      return buildOptimizationParams(state, ctx);
    case 'mix_and_match':
      return buildMixAndMatchParams(state, ctx);
    case 'update_products':
      return buildUpdateProductsParams(state, ctx);
    default:
      return null;
  }
};
